import { DropdownProps, Icon, ModalProps, Portal } from '@toggle/design-system';
import React, { Fragment, ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import * as S from '~/components/search-modal/SearchModal.styles';
import { useDropdownKeyboardEvents } from '~/hooks/use-dropdown-keyboard-events/useDropdownKeyboardEvents';

export interface SearchModalBaseProps<T> extends ModalProps {
  searchValue: string;
  handleSearch: (value: string) => void;
  selectItem: DropdownProps<T>['selectItem'];
  dropdownItems: DropdownProps<T>['dropdownItems'];
  isLoading: boolean;
  noSearchResultsLabel: ReactNode;
  renderDropdownItem: DropdownProps<T>['renderDropdownItem'];
  value?: string;
  placeholder?: string;
  focusedItemIdx?: number;
  beforeResultsNode?: DropdownProps<T>['beforeResultsNode'];
  afterInListNode?: DropdownProps<T>['afterInListNode'];
  shouldCloseOnSelect?: DropdownProps<T>['shouldCloseOnSelect'];
  loadingNode?: ReactNode;
  shouldResetScrollOnItemsChange?: boolean;
  searchInputVariant?: 'default' | 'secondary';
  showHeader?: boolean;
}

export const SearchModalBase = <T,>({
  dropdownItems,
  searchValue,
  value = searchValue,
  isLoading,
  placeholder,
  beforeResultsNode,
  noSearchResultsLabel,
  shouldCloseOnSelect,
  afterInListNode,
  focusedItemIdx = -1,
  loadingNode,
  shouldResetScrollOnItemsChange = true,
  searchInputVariant = 'default',
  showHeader = true,
  selectItem,
  handleSearch,
  renderDropdownItem,
  ...rest
}: SearchModalBaseProps<T>) => {
  const { t } = useTranslation('common');
  const showNoSearchResults =
    !!value.length && !dropdownItems.length && !isLoading;
  const [focusedText, setFocusedText] = useState<string>();
  const isSecondaryVariant = searchInputVariant === 'secondary';

  const wrappedBeforeResultsNode =
    isSecondaryVariant && !showHeader ? (
      <S.BeforeResultsWrapper $hasRoundedCorners>
        {beforeResultsNode}
      </S.BeforeResultsWrapper>
    ) : (
      beforeResultsNode
    );

  useEffect(() => {
    if (focusedText) {
      setFocusedText(undefined);
    }
  }, [searchValue, value, beforeResultsNode]);

  const hasInput = !!focusedText?.length || !!searchValue.length;

  const handleClear = () => {
    handleSearch('');

    if (focusedText) {
      setFocusedText(undefined);
    }
  };

  const clearButton = hasInput && (
    <S.StyledButton
      data-testid="clear-button"
      tabIndex={-1}
      onClick={handleClear}
      iconName="CloseCircle"
      size="small"
      variant="empty"
    />
  );

  const topBarRightButtons = hasInput && (
    <S.TopBarButtonsContainer>
      <S.TopBarClearButton
        data-testid="top-bar-clear-button"
        onClick={handleClear}
      >
        {t('searchClear')}
      </S.TopBarClearButton>
      <S.TopBarDivider />
      <S.TopBarCloseButton
        data-testid="top-bar-close-button"
        iconName="Close"
        onClick={rest.onClose}
        size={12}
      />
    </S.TopBarButtonsContainer>
  );

  const dropdownApiRef = useDropdownKeyboardEvents();

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleSearch(e.target.value);

    if (focusedText) {
      setFocusedText(undefined);
    }
  };

  // Search input for default variant (inside modal)
  const defaultSearchInput = (
    <S.StyledAutocompleteInput
      data-testid="search-input"
      value={focusedText ?? searchValue}
      onChange={handleInputChange}
      placeholder={placeholder}
      leftIcon={<Icon iconName="Search" size={20} />}
      rightIcon={clearButton}
      autoFocus
      $autocompleteInputVariant={'secondary'}
      $showHeader={showHeader}
    />
  );

  // Search input for secondary variant (in top bar)
  const topBarSearchInput = (
    <Portal>
      <S.TopBarSearchContainer>
        <S.TopBarSearchInput
          data-testid="search-input"
          value={focusedText ?? searchValue}
          onChange={handleInputChange}
          placeholder={placeholder}
          leftIcon={<Icon iconName="Search" size={20} />}
          rightIcon={topBarRightButtons}
          autoFocus
        />
      </S.TopBarSearchContainer>
    </Portal>
  );

  if (isLoading && loadingNode) {
    return (
      <Fragment>
        {isSecondaryVariant && topBarSearchInput}
        <S.StyledModal
          $inChartPage={false}
          $searchInputVariant={searchInputVariant}
          $showHeader={showHeader}
          {...rest}
        >
          {loadingNode}
        </S.StyledModal>
      </Fragment>
    );
  }

  return (
    <Fragment>
      {isSecondaryVariant && topBarSearchInput}
      <S.StyledModal
        $inChartPage={false}
        $searchInputVariant={searchInputVariant}
        $showHeader={showHeader}
        {...rest}
      >
        <S.StyledDropdown<T>
          enableFloating={false}
          shouldCloseOnOutsideClick={false}
          shouldCloseOnSelect={shouldCloseOnSelect}
          shouldResetScrollOnItemsChange={shouldResetScrollOnItemsChange}
          shouldDisableItemMouseEvents
          selectItem={selectItem}
          dropdownItems={dropdownItems}
          autocompleteInputValue={value}
          focusedItemIdx={focusedItemIdx}
          $isSecondaryVariant={isSecondaryVariant}
          renderDropdownItem={(item, isActive, isFocused, isDisabled) => {
            return renderDropdownItem(
              item,
              isActive,
              isFocused,
              isDisabled,
              setFocusedText
            );
          }}
          beforeResultsNode={wrappedBeforeResultsNode}
          afterResultsNode={
            showNoSearchResults && (
              <S.HelpText data-testid="no-search-results">
                {noSearchResultsLabel}
              </S.HelpText>
            )
          }
          afterInListNode={afterInListNode}
          dropdownApiRef={dropdownApiRef}
        >
          {isSecondaryVariant ? <Fragment /> : defaultSearchInput}
        </S.StyledDropdown>
      </S.StyledModal>
    </Fragment>
  );
};
